import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildYogiSteps, resolveYogiText } from '../yogiSteps.js';

/**
 * @param {{ firstName?: string, name?: string, talkedWithYogi?: boolean } | null | undefined} user
 */
export function useYogiChat(user) {
  const steps = useMemo(() => buildYogiSteps(user || {}), [user?.firstName, user?.name, user?.talkedWithYogi]);

  const [messages, setMessages] = useState(/** @type {import('../yogiTypes.js').YogiMessage[]} */ ([]));
  const [pendingOptions, setPendingOptions] = useState(/** @type {import('../yogiTypes.js').YogiOption[] | null} */ (null));
  const [pendingOptionsMeta, setPendingOptionsMeta] = useState(/** @type {{ storeAs?: string } | null} */ (null));
  const [activeComponent, setActiveComponent] = useState(/** @type {import('../yogiTypes.js').YogiComponentStep | null} */ (null));
  const [context, setContext] = useState(/** @type {import('../yogiTypes.js').YogiContext} */ ({}));
  const [isEnded, setIsEnded] = useState(false);
  const initialized = useRef(false);

  const appendBotMessage = useCallback((text) => {
    setMessages((prev) => [...prev, { role: 'bot', text }]);
  }, []);

  const appendUserMessage = useCallback((text) => {
    setMessages((prev) => [...prev, { role: 'user', text }]);
  }, []);

  const showOptions = useCallback((stepId, ctx) => {
    const step = steps[stepId];
    if (step?.type !== 'options') return;
    setPendingOptions(step.options);
    setPendingOptionsMeta({ storeAs: step.storeAs });
    setActiveComponent(null);
  }, [steps]);

  const showComponent = useCallback((step) => {
    setPendingOptions(null);
    setPendingOptionsMeta(null);
    setActiveComponent(step);
  }, []);

  const enterStep = useCallback(
    (stepId, ctx) => {
      const step = steps[stepId];
      if (!step) return;

      if (step.type === 'message') {
        appendBotMessage(resolveYogiText(step.message, ctx));
        if (step.end) {
          setIsEnded(true);
          setPendingOptions(null);
          setActiveComponent(null);
          return;
        }
        if (!step.trigger) return;
        const next = steps[step.trigger];
        if (next?.type === 'options') showOptions(step.trigger, ctx);
        else if (next?.type === 'component') showComponent(next);
        else if (next?.type === 'message') enterStep(step.trigger, ctx);
        return;
      }

      if (step.type === 'options') {
        showOptions(stepId, ctx);
        return;
      }

      if (step.type === 'component') {
        showComponent(step);
      }
    },
    [appendBotMessage, showComponent, showOptions, steps],
  );

  const handleOptionSelect = useCallback(
    (option) => {
      appendUserMessage(option.label);
      setPendingOptions(null);

      const nextContext = { ...context, previousValue: option.label };
      if (pendingOptionsMeta?.storeAs === 'symptom') {
        nextContext.symptom = option.label;
      }
      setContext(nextContext);
      setPendingOptionsMeta(null);

      const nextStep = steps[option.trigger];
      if (!nextStep) return;

      if (nextStep.type === 'message') {
        enterStep(option.trigger, nextContext);
        return;
      }

      if (nextStep.type === 'options') {
        enterStep(option.trigger, nextContext);
        return;
      }

      if (nextStep.type === 'component') {
        if (nextStep.component === 'thankYouSorry') {
          const symptom = nextContext.symptom || nextContext.previousValue;
          appendBotMessage(
            `Thanks! I am sorry that you are having trouble with ${symptom}. Even just a few minutes of yoga can improve your ${symptom}! Would you like to do some yoga now?`,
          );
          if (nextStep.trigger) showOptions(nextStep.trigger, nextContext);
          return;
        }

        if (nextStep.component === 'itsOk') {
          const symptom = nextContext.symptom;
          appendBotMessage(
            symptom
              ? `That's ok. We are here for you all-day everyday. You can come back whenever you need help with your ${symptom}.`
              : "That's ok. We are here for you all-day everyday. You can come back whenever you need help.",
          );
          setIsEnded(true);
          return;
        }

        showComponent(nextStep);
      }
    },
    [appendBotMessage, appendUserMessage, context, enterStep, pendingOptionsMeta?.storeAs, showComponent, showOptions, steps],
  );

  const handleComponentComplete = useCallback(
    (value) => {
      const component = activeComponent;
      if (!component) return;

      const nextContext = value ? { ...context, previousValue: value } : context;
      if (value) setContext(nextContext);

      setActiveComponent(null);

      if (component.end) {
        setIsEnded(true);
        return;
      }

      if (component.trigger) {
        enterStep(component.trigger, nextContext);
      }
    },
    [activeComponent, context, enterStep],
  );

  const reset = useCallback(() => {
    initialized.current = false;
    setMessages([]);
    setPendingOptions(null);
    setPendingOptionsMeta(null);
    setActiveComponent(null);
    setContext({});
    setIsEnded(false);
  }, []);

  useEffect(() => {
    if (!user || initialized.current) return;
    initialized.current = true;
    enterStep('0', {});
  }, [user, enterStep]);

  return {
    messages,
    pendingOptions,
    activeComponent,
    context,
    isEnded,
    handleOptionSelect,
    handleComponentComplete,
    reset,
  };
}
